import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao, Cartao } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { anunciarOcultos } from '../../componentes/sensivel';
import { get } from '../../nucleo/api';
import { dataHora, nomePessoa } from '../../nucleo/formato';
import { censurarCampo } from '../../nucleo/censura-json';
import { rotuloAcao, rotuloCampo, rotuloValor } from '../../nucleo/rotulos';
import { nomeDoPapel, type EntradaAuditoria, type EntradaHistoricoAparencia, type NomesReferencias } from './tipos';
import i18n from 'i18next';
import { telaDaVisao } from '../../nucleo/nome-da-tela';
import { carregarMais } from '../../nucleo/foco';

interface Pagina<T> {
  itens: T[];
  proximo_cursor: string | null;
}

const curto = (s: string) => (s.length > 60 ? `${s.slice(0, 57)}...` : s);

/** Ids de papéis ou pessoas pelo nome atual; o que não se acha (removido, fora do workspace) vira "(removido)". */
function nomesDaVisao(campo: string, v: unknown, nomes: NomesReferencias | undefined): string | null {
  if (!nomes || (campo !== 'papeis' && campo !== 'pessoas') || !Array.isArray(v) || v.length === 0) return null;
  const t = i18n.getFixedT(null, 'configuracoes');
  return v
    .map((id) => {
      if (campo === 'papeis') {
        const p = nomes.papeis[String(id)];
        return p ? nomeDoPapel(t, p) : t('historico.removido');
      }
      // Review r45 visual (M1): o nome da pessoa passa pelo modo discreto (e-mail como nome, opção de iniciais), como nas caixas.
      const n = nomes.pessoas[String(id)];
      return n ? nomePessoa(n) : t('historico.removido');
    })
    .join(', ');
}

/** Lista os campos que mudaram entre dois instantâneos, no formato `Campo: de → para`, com nomes e valores legíveis. */
export function resumirAlteracao(antes: unknown, depois: unknown, entidade = 'configuracoes', nomes?: NomesReferencias): string[] {
  const a = (antes && typeof antes === 'object' ? antes : {}) as Record<string, unknown>;
  const d = (depois && typeof depois === 'object' ? depois : {}) as Record<string, unknown>;
  const chaves = [...new Set([...Object.keys(a), ...Object.keys(d)])];
  // Visões salvas usam o caminho do menu, como a lista; o formatador genérico continua independente do registro de módulos.
  // Captura do Gustavo (13/10): papéis e pessoas da visão pelo nome (o registro guarda os ids), nunca o código.
  const valor = (campo: string, v: unknown) => telaDaVisao(entidade, campo, v, i18n.t.bind(i18n)) ?? nomesDaVisao(campo, v, nomes) ?? rotuloValor(campo, v);
  return chaves
    .filter((c) => JSON.stringify(a[c] ?? null) !== JSON.stringify(d[c] ?? null))
    // Modo discreto (r10 visual B2): a mesma censura da Auditoria, com os campos ao lado (o número da casa de um endereço).
    .map((c) => {
      const de = curto(valor(c, censurarCampo(c, a[c], entidade, a)));
      const para = curto(valor(c, censurarCampo(c, d[c], entidade, d)));
      return c in a ? `${rotuloCampo(c)}: ${de} → ${para}` : `${rotuloCampo(c)}: ${para}`;
    });
}

/**
 * Quem fez a alteração: o usuário; a chave de API com nome e prefixo (como na auditoria do Admin);
 * ou o sistema. Review r3 (spec): ação por chave de API aparecia sem autor.
 */
export function rotuloAutor(i: Pick<EntradaAuditoria, 'ator_tipo' | 'autor_nome' | 'autor_prefixo'>, t: (chave: string) => string): string {
  if (i.ator_tipo === 'chave_api') return [t('historico.chaveApi'), i.autor_nome, i.autor_prefixo && `(${i.autor_prefixo})`].filter(Boolean).join(' ');
  if (i.ator_tipo === 'sistema' || !i.autor_nome) return t('historico.sistema');
  return i.autor_nome;
}

/**
 * M13.6: histórico de cada tela, vindo da auditoria do módulo. `linhas` troca o resumo genérico (campo: de → para) por um
 * próprio da tela (fase 2, W2b: o compartilhamento escreve tipo e modo no idioma do app).
 */
export function Historico({ entidade, titulo, entidadeId, linhas: resumir }: { entidade: string; titulo?: string; entidadeId?: string; linhas?: (i: EntradaAuditoria) => string[] }) {
  const { t } = useTranslation('configuracoes');
  const { t: tn } = useTranslation();
  const q = useInfiniteQuery({
    queryKey: ['configuracoes', 'historico', entidade, entidadeId],
    queryFn: ({ pageParam }) => get<Pagina<EntradaAuditoria>>('/configuracoes/historico', { entidade, entidade_id: entidadeId, limite: 15, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.proximo_cursor ?? undefined,
  });
  const itens = q.data?.pages.flatMap((p) => p.itens) ?? [];
  return (
    <Cartao titulo={titulo ?? t('historico.titulo')}>
      {q.isPending ? (
        <Carregando />
      ) : q.isError ? (
        <Erro erro={q.error} aoTentar={() => q.refetch()} />
      ) : itens.length === 0 ? (
        <p className="text-sm text-tinta-3">{t('historico.vazio')}</p>
      ) : (
        <>
          <ul className="divide-y divide-borda">
            {itens.map((i) => {
              const linhas = i.depois === null || i.depois === undefined ? [] : resumir ? resumir(i) : resumirAlteracao(i.antes, i.depois, entidade, i.nomes);
              return (
                <li key={i.id} className="flex min-w-0 flex-col gap-1 py-2.5 text-sm">
                  <p className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium">{t(`historico.acoes.${i.acao}`, { defaultValue: rotuloAcao(i.acao) })}</span>
                    <span className="text-xs text-tinta-3">
                      {t('historico.por', { nome: rotuloAutor(i, t) })} · {dataHora(i.created_at)}
                    </span>
                  </p>
                  {linhas.length > 0 && (
                    <ul className="min-w-0 text-xs text-tinta-2">
                      {linhas.slice(0, 4).map((l) => (
                        <li key={l} className="break-words">
                          {anunciarOcultos(l)}
                        </li>
                      ))}
                      {linhas.length > 4 && <li className="text-tinta-3">+{linhas.length - 4}</li>}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
          {q.hasNextPage && (
            <div className="flex justify-center border-t border-borda pt-3">
              <Botao variante="fantasma" carregando={q.isFetchingNextPage} onClick={(e) => carregarMais(() => q.fetchNextPage(), e.currentTarget)}>
                {tn('comum.carregarMais')}
              </Botao>
            </div>
          )}
        </>
      )}
    </Cartao>
  );
}

/** Histórico da chave `aparencia` (valor anterior e novo, gravado pelo núcleo em configuracoes_historico). */
export function HistoricoAparencia() {
  const { t } = useTranslation('configuracoes');
  const { t: tn } = useTranslation();
  const q = useInfiniteQuery({
    queryKey: ['configuracoes', 'aparencia', 'historico'],
    queryFn: ({ pageParam }) => get<Pagina<EntradaHistoricoAparencia>>('/configuracoes/aparencia/historico', { limite: 15, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.proximo_cursor ?? undefined,
  });
  const itens = q.data?.pages.flatMap((p) => p.itens) ?? [];
  return (
    <Cartao titulo={t('aparencia.historico')}>
      {q.isPending ? (
        <Carregando />
      ) : q.isError ? (
        <Erro erro={q.error} aoTentar={() => q.refetch()} />
      ) : itens.length === 0 ? (
        <p className="text-sm text-tinta-3">{t('historico.vazio')}</p>
      ) : (
        <>
          <ul className="divide-y divide-borda">
            {itens.map((i) => (
              <li key={i.id} className="flex min-w-0 flex-col gap-1 py-2.5 text-sm">
                <span className="text-xs text-tinta-3">
                  {t('historico.por', { nome: i.autor_nome ?? t('historico.sistema') })} · {dataHora(i.created_at)}
                </span>
                {/* Fase 2 (W4): o hash da fonte e da logo é uma palavra só de 64 caracteres; quebra em qualquer ponto a 390 px. */}
                <ul className="min-w-0 text-xs text-tinta-2">
                  {resumirAlteracao(i.valor_anterior, i.valor_novo).map((l) => (
                    <li key={l} className="wrap-anywhere">
                      {anunciarOcultos(l)}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          {q.hasNextPage && (
            <div className="flex justify-center border-t border-borda pt-3">
              <Botao variante="fantasma" carregando={q.isFetchingNextPage} onClick={(e) => carregarMais(() => q.fetchNextPage(), e.currentTarget)}>
                {tn('comum.carregarMais')}
              </Botao>
            </div>
          )}
        </>
      )}
    </Cartao>
  );
}
